import React, { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import AltRetailerLogo from 'images/vr-new-retail_3.png'
import { MoreHorizontal, ChevronUp, ChevronDown, ArrowUpDown, MapPin } from 'lucide-react'
import MapLocationPopupCN from 'components/popups/MapLocationPopupCN'

import useTable from 'components/controls/useTable'
import Layout from 'components/Layout'
import Spinner from 'components/Spinner'
import Message from 'components/Message'
import Popup from 'components/Popup'
import { initializeMyProjects } from 'slices/projectSlice'
import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell,
} from 'components/ui/table'
import { TablePagination } from 'components/ui/pagination'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'

const headerCells = [
	{ id: 'Image', label: '', sortable: false },
	{ id: 'projectName', label: 'Project Name' },
	{ id: 'assetName', label: 'Asset Name' },
	{ id: 'address', label: 'Address' },
	{ id: 'city', label: 'City' },
	{ id: 'stateProvince', label: 'State' },
	{ id: 'postalCode', label: 'Postal Code' },
	{ id: 'isActive', label: 'Status' },
	{ id: 'reLoad', label: 'Actions', sortable: false },
]

const options = ['Edit/Modify', 'Delete']

const ProjectsList = () => {
	const dispatch = useDispatch()

	const [openModifyPopup, setOpenModifyPopup] = useState(false)
	const [openArchivePopup, setOpenArchivePopup] = useState(false)
	const [openDeletePopup, setOpenDeletePopup] = useState(false)
	const [popupTitle, setPopupTitle] = useState('')
	const [recordForEdit, setRecordForEdit] = useState(null)

	const searchInputRef = useRef(null)
	const [searchInput, setSearchInput] = useState('')

	const [orderBy, setOrderBy] = useState(null)
	const [order, setOrder] = useState('asc')
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)

	// Map popup state
	const [openMap, setOpenMap] = useState(false)
	const [mapLocation, setMapLocation] = useState(null)

	const handleOpenMap = (e, loc) => {
		e.stopPropagation()
		setMapLocation(loc)
		setOpenMap(true)
	}

	const [filterFn, setFilterFn] = useState({
		fn: items => {
			return items
		},
	})

	const userLogin = useSelector(state => state.userLogin)
	const { userInfo: user } = userLogin
	const userId = user._id

	const { entities: projects, error, ids: projectIds, isLoading, isInitialized } = useSelector(
		state => state.projects,
	)

	const newProjects = projectIds.map(id => projects[id])

	var records = []

	if (newProjects.length > 0) {
		records = newProjects.map(item => {
			return {
				_id: item._id,
				projectName: item.projectName,
				assetName: item.projectAsset.assetName,
				projectType: item.projectType,
				address: item.projectAsset.address,
				city: item.projectAsset.city,
				stateProvince: item.projectAsset.stateProvince,
				postalCode: item.projectAsset.postalCode,
				image: item.projectAsset.image,
				latitude: item.projectAsset.latitude,
				longitude: item.projectAsset.longitude,
				owner: item.owner._id,
				ownerImage: item.owner.image,
			}
		})
	}

	useEffect(() => {
		dispatch(initializeMyProjects())
	}, [dispatch, isInitialized])

	const hasRows = records ? records.length : null
	const recordCount = records.length
	const { recordsAfterPagingAndSorting } = useTable(
		records,
		headerCells,
		filterFn,
		true
	)

	const handleSort = columnId => {
		if (orderBy === columnId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const getNestedValue = (obj, path) => {
		return path.split('.').reduce((acc, key) => acc?.[key], obj)
	}

	const filteredRecords = recordsAfterPagingAndSorting()

	const sortedRecords = useMemo(() => {
		if (!orderBy) return filteredRecords
		return [...filteredRecords].sort((a, b) => {
			const aVal = (getNestedValue(a, orderBy) || '').toString().toLowerCase()
			const bVal = (getNestedValue(b, orderBy) || '').toString().toLowerCase()
			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [filteredRecords, orderBy, order])

	const paginatedRecords = useMemo(() => {
		const start = page * rowsPerPage
		return sortedRecords.slice(start, start + rowsPerPage)
	}, [sortedRecords, page, rowsPerPage])

	const SortIcon = ({ columnId }) => {
		if (orderBy !== columnId) return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ChevronUp className="ml-1 h-3 w-3 text-primary" />
		) : (
			<ChevronDown className="ml-1 h-3 w-3 text-primary" />
		)
	}

	const handleSearchAll = e => {
		const { value } = e.target
		setSearchInput(value)
		setFilterFn({
			fn: items => {
				if (value === '') return items
				const lowerValue = value.toLowerCase()
				return items.filter(
					x =>
						(x.projectName && x.projectName.toLowerCase().includes(lowerValue)) ||
						(x.assetName && x.assetName.toLowerCase().includes(lowerValue)) ||
						(x.address && x.address.toLowerCase().includes(lowerValue)) ||
						(x.city && x.city.toLowerCase().includes(lowerValue)) ||
						(x.stateProvince && x.stateProvince.toLowerCase().includes(lowerValue)) ||
						(x.postalCode && x.postalCode.toLowerCase().includes(lowerValue)),
				)
			},
		})
		setTimeout(() => {
			searchInputRef.current.focus()
		}, 0)
	}

	return (
		<div className="space-y-2">
			<Layout>
				<Layout.Page>
					<Layout.Body>
						{isLoading ? (
							<Spinner containerHeight="50vh" />
						) : !records ? (
							<Message variant="danger">{error}</Message>
						) : error ? (
							<Message variant="danger">{error}</Message>
						) : recordCount ? (
							<>
								<div>
									<div className="grid grid-cols-12 gap-6">
										<div className="col-span-12">
											<Layout.PageToolbarFilter
												searchInputLabel="Search:"
												searchInputRef={searchInputRef}
												searchInputValue={searchInput}
												searchInputOnChange={handleSearchAll}
											/>

											<Table>
												<TableHeader>
													<TableRow className="border-b-2 border-[#027fff] hover:bg-transparent">
														{headerCells.map(cell => (
															<TableHead
																key={cell.id}
																className={
																	cell.sortable !== false
																		? 'cursor-pointer select-none text-gray-700 text-xs font-semibold whitespace-nowrap'
																		: 'text-gray-700 text-xs font-semibold whitespace-nowrap'
																}
																onClick={cell.sortable !== false ? () => handleSort(cell.id) : undefined}
															>
																<span className="inline-flex items-center">
																	{cell.label}
																	{cell.sortable !== false && <SortIcon columnId={cell.id} />}
																</span>
															</TableHead>
														))}
													</TableRow>
												</TableHeader>
												<TableBody>
													{paginatedRecords.map(project => (
														<TableRow key={project.assetName} className="h-7 hover:bg-[#fffbf2] cursor-pointer">
															<TableCell className="py-1 text-sm">
																<Avatar>
																	<AvatarImage src={project.image || AltRetailerLogo} />
																	<AvatarFallback />
																</Avatar>
															</TableCell>

															<TableCell className="py-1 text-sm">{project.projectName}</TableCell>
															<TableCell className="py-1 text-sm">{project.assetName}</TableCell>
															<TableCell className="py-1 text-sm">
															<button
																type="button"
																onClick={e => handleOpenMap(e, project)}
																className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600 hover:bg-[#0080fe]/10 hover:text-[#0080fe] hover:border-[#0080fe]/20 transition-colors cursor-pointer"
															>
																<MapPin className="h-3 w-3" />
																{project.address}
															</button>
														</TableCell>
															<TableCell className="py-1 text-sm">{project.city}</TableCell>
															<TableCell className="py-1 text-sm">{project.stateProvince}</TableCell>
															<TableCell className="py-1 text-sm">{project.postalCode}</TableCell>
															<TableCell className="py-1 text-sm">{project.isActive ? 'Active' : 'In Active'}</TableCell>
															<TableCell className="py-1 text-sm">
																<MenuButton
																	project={project}
																	setPopupTitle={setPopupTitle}
																	setRecordForEdit={setRecordForEdit}
																	setOpenDeletePopup={setOpenDeletePopup}
																	setOpenModifyPopup={setOpenModifyPopup}
																/>
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
											{hasRows > 4 ? (
												<TablePagination
													page={page}
													rowsPerPage={rowsPerPage}
													count={sortedRecords.length}
													onPageChange={setPage}
													onRowsPerPageChange={setRowsPerPage}
												/>
											) : null}
										</div>
									</div>
								</div>
							</>
						) : null}
					</Layout.Body>
				</Layout.Page>
			</Layout>

			{openMap && mapLocation && (
				<MapLocationPopupCN
					open={openMap}
					onClose={() => setOpenMap(false)}
					location={mapLocation}
				/>
			)}
		</div>
	)
}

const MenuButton = ({
	project,
	setPopupTitle,
	setRecordForEdit,
	setOpenArchivePopup,
	setOpenDeletePopup,
	setOpenModifyPopup,
}) => {
	const handleAction = (option, project) => {
		switch (option) {
			case 'Delete':
				setPopupTitle(`Delete Location: ${project.assetName}`)
				setRecordForEdit(project)
				setOpenDeletePopup(true)
				break

			case 'Edit/Modify':
				setPopupTitle(`Edit/Modify Location: ${project.assetName}`)
				setRecordForEdit(project)
				setOpenModifyPopup(true)
				break

			default:
				break
		}
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					className="inline-flex items-center justify-center rounded-full p-1 text-[#0080fe] hover:bg-gray-100 transition-colors"
					aria-label="more"
				>
					<MoreHorizontal className="h-5 w-5" />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-[15ch]">
				{options.map(option => (
					<DropdownMenuItem
						key={option}
						onClick={() => handleAction(option, project)}
						className="text-[#0080fe] cursor-pointer"
					>
						{option}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

export default ProjectsList
